"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { SongwriterBlock } from "../types/songwriter.types";
import {
  Camera,
  Shot,
  ShotPart,
  ShotCharacter,
} from "../../types/shot-breakdown.types";
import { uuid } from "../../types/screenplay-editor.types";

export interface UseLyricalBreakdownProps {
  blocks: SongwriterBlock[];
  initialShots?: Shot[];
  initialCameras?: Camera[];
  initialMovieCharacters?: ShotCharacter[];
  initialSceneType?: string;
  onShotAdded?: (shot: Shot) => any;
  onShotUpdated?: (shotId: number | string, shot: Shot) => any;
  onProductionInitialize?: (count: number, cameras: Camera[]) => any;
  deleteShot?: (shotId: number | string) => void;
}

export function useLyricalBreakdown({
  blocks: passedBlocks,
  initialShots,
  initialCameras,
  initialMovieCharacters,
  initialSceneType,
  deleteShot: passedDeleteShot,
  onShotAdded,
  onShotUpdated,
  onProductionInitialize,
}: UseLyricalBreakdownProps) {
  const [blocks, setBlocks] = useState<SongwriterBlock[]>([]);
  const [shots, setShots] = useState<Shot[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [movieCharacters, setMovieCharacters] = useState<ShotCharacter[]>([]);
  const [sceneType, setSceneType] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [autosaveStatus, setAutosaveStatus] = useState<
    "idle" | "typing" | "saving" | "saved" | "error"
  >("idle");

  const [selectionMenu, setSelectionMenu] = useState<{
    top: number;
    left: number;
    parts: ShotPart[];
  } | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  const clearSelection = useCallback(() => {
    setSelectionMenu(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  // Handle outside mousedown to dismiss tooltip
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        clearSelection();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [clearSelection]);

  // Translate Node offsets into pure text indices
  const getAbsoluteOffset = useCallback(
    (blockEl: Element, targetNode: Node, targetOffset: number) => {
      let currentOffset = 0;
      const walker = document.createTreeWalker(
        blockEl,
        NodeFilter.SHOW_TEXT,
        null,
      );
      let currentNode = walker.nextNode();
      while (currentNode) {
        if (currentNode === targetNode) {
          return currentOffset + targetOffset;
        }
        currentOffset += currentNode.nodeValue?.length || 0;
        currentNode = walker.nextNode();
      }
      return targetOffset;
    },
    [],
  );

  // Initialize and filter blocks when passedBlocks changes
  useEffect(() => {
    if (passedBlocks && passedBlocks.length > 0) {
      const filteredBlocks = passedBlocks.filter(
        (block: SongwriterBlock) =>
          block.type === "SONG_LYRICS" || block.type === "SONG_BEAT",
      );
      setBlocks(filteredBlocks);
    } else {
      setBlocks([]);
    }
  }, [passedBlocks]);

  // Sync state with incoming props
  useEffect(() => {
    if (initialShots) {
      setShots(initialShots);
    }
  }, [initialShots]);

  useEffect(() => {
    if (initialCameras) {
      setCameras(initialCameras);
    }
  }, [initialCameras]);

  useEffect(() => {
    if (initialMovieCharacters) {
      setMovieCharacters(initialMovieCharacters);
    }
  }, [initialMovieCharacters]);

  useEffect(() => {
    if (initialSceneType) {
      setSceneType(initialSceneType);
    }
  }, [initialSceneType]);

  // Set loading state to false on client-side mount
  useEffect(() => {
    setIsLoading(false);
  }, []);

  // Capture selections inside lyric elements on mouse release
  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    const startNode = range.startContainer;
    const endNode = range.endContainer;

    const startBlockEl =
      startNode.nodeType === Node.TEXT_NODE
        ? startNode.parentElement?.closest("[data-block-id]")
        : (startNode as Element).closest("[data-block-id]");
    const endBlockEl =
      endNode.nodeType === Node.TEXT_NODE
        ? endNode.parentElement?.closest("[data-block-id]")
        : (endNode as Element).closest("[data-block-id]");

    if (!startBlockEl || !endBlockEl) return;

    const startBlockId = startBlockEl.getAttribute("data-block-id");
    const endBlockId = endBlockEl.getAttribute("data-block-id");

    const startIndexInArray = blocks.findIndex((b) => b.id === startBlockId);
    const endIndexInArray = blocks.findIndex((b) => b.id === endBlockId);

    if (startIndexInArray === -1 || endIndexInArray === -1) return;

    const startAbsOffset = getAbsoluteOffset(
      startBlockEl,
      startNode,
      range.startOffset,
    );
    const endAbsOffset = getAbsoluteOffset(
      endBlockEl,
      endNode,
      range.endOffset,
    );

    const selectedParts: ShotPart[] = [];

    for (let i = startIndexInArray; i <= endIndexInArray; i++) {
      const block = blocks[i];
      const start = i === startIndexInArray ? startAbsOffset : 0;
      const end = i === endIndexInArray ? endAbsOffset : block.text.length;

      if (start !== end) {
        selectedParts.push({
          block_id: block.id,
          start_index: start,
          end_index: end,
          text: block.text.slice(start, end),
        });
      }
    }

    if (selectedParts.length > 0) {
      const rect = range.getBoundingClientRect();
      setSelectionMenu({
        top: rect.top + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX,
        parts: selectedParts,
      });
    }
  }, [blocks, getAbsoluteOffset]);

  // Updates the timeline array in local state
  const saveShotsWithStatusAnimation = useCallback((updatedShots: Shot[]) => {
    setShots(updatedShots);
  }, []);

  // Initializes cameras dynamically
  const initializeProduction = useCallback(
    async (count: number, type: string) => {
      const newCameras: Camera[] = [];
      for (let i = 1; i <= count; i++) {
        newCameras.push({ name: `Camera ${String.fromCharCode(64 + i)}` });
      }

      const result = onProductionInitialize?.(count, newCameras);
      if (result instanceof Promise) {
        setCameras(newCameras);
        setSceneType(type);
      }
    },
    [],
  );

  // Adds a shot to the persistent timeline
  const addShot = (shotDetails: Omit<Shot, "id" | "parts">) => {
    if (!selectionMenu) return;
    const newShot: Shot = {
      ...shotDetails,
      id: uuid(),
      parts: selectionMenu.parts,
    };
    setShots((prev) => [...prev, newShot]);

    const result = onShotAdded?.(newShot);
    if (result instanceof Promise) {
      return result.then(() => {
        clearSelection();
      });
    } else {
      clearSelection();
      return result;
    }
  };

  // Edits an existing shot's details
  const updateShot = async (
    shotId: string | number,
    updatedDetails: Omit<Shot, "id" | "parts">,
  ) => {
    let updatedShot: Shot | null = null;
    setShots((prev) =>
      prev.map((shot) => {
        if (shot.id === shotId) {
          updatedShot = { ...shot, ...updatedDetails };
          return updatedShot;
        }
        return shot;
      }),
    );

    if (updatedShot) {
      const result = onShotUpdated?.(shotId, updatedShot);
      if (result instanceof Promise) {
        await result;
      }
      return result;
    }
  };

  // Deletes a shot and sequentially re-orders shot numbers
  const deleteShot = useCallback(
    (shotId: string | number) => {
      if (passedDeleteShot) {
        passedDeleteShot(shotId);
      } else {
        const filtered = shots.filter((shot) => shot.id !== shotId);

        // Resequence remaining shot numbers
        const reordered = filtered.map((shot, idx) => ({
          ...shot,
          shot_number: idx + 1,
        }));

        saveShotsWithStatusAnimation(reordered);
      }
    },
    [shots, saveShotsWithStatusAnimation, passedDeleteShot],
  );

  return {
    blocks,
    isLoading,
    shots,
    cameras,
    movieCharacters,
    sceneType,
    autosaveStatus,
    selectionMenu,
    clearSelection,
    handleMouseUp,
    initializeProduction,
    addShot,
    updateShot,
    deleteShot,
    menuRef,
  };
}
